/**
 * Display helpers.
 *
 * Two units, and they must never be confused in the UI:
 *
 *   PRICE  — 1..99, the cost of one contract in cents. Shown as a percentage,
 *            because for a binary contract the price *is* the market's implied
 *            probability. "60%" is immediately meaningful in a way that "60¢"
 *            is not, and it is what every reader already knows how to read.
 *
 *   CREDITS — bigint minor units. One contract pays out 100 minor units on the
 *            winning side, so amounts are shown as `units / 100` with two
 *            decimals. Formatting is done on the integer, never by converting
 *            to a float.
 */

export function probability(price: number | null | undefined): string {
  if (price === null || price === undefined) return '—';
  return `${price}%`;
}

/** Format minor units as credits, e.g. 123456n -> "1,234.56". */
export function credits(units: bigint): string {
  const negative = units < 0n;
  const abs = negative ? -units : units;
  const whole = abs / 100n;
  const frac = abs % 100n;
  const grouped = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '-' : ''}${grouped}.${frac.toString().padStart(2, '0')}`;
}

/** Cost of `qty` contracts at `price` cents each, in minor units. */
export function costOf(price: number, qty: number): bigint {
  return BigInt(price) * BigInt(qty);
}

/** A winning contract redeems for 100 minor units. */
export function payoutOf(qty: number): bigint {
  return 100n * BigInt(qty);
}

export function relativeTime(iso: string, now = Date.now()): string {
  const delta = new Date(iso).getTime() - now;
  const abs = Math.abs(delta);
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;

  const say = (n: number, unit: string) => {
    const rounded = Math.round(n);
    const plural = rounded === 1 ? unit : `${unit}s`;
    return delta > 0 ? `in ${rounded} ${plural}` : `${rounded} ${plural} ago`;
  };

  if (abs < minute) return delta > 0 ? 'in a moment' : 'just now';
  if (abs < hour) return say(abs / minute, 'minute');
  if (abs < day) return say(abs / hour, 'hour');
  return say(abs / day, 'day');
}

/**
 * A trade's time. Shows the clock alone for today, and the date as well for
 * anything older — a list spanning several days that shows only "07:47 AM"
 * twice is actively misleading about when a price was set.
 */
export function tradeTime(iso: string, now = new Date()): string {
  const at = new Date(iso);
  const sameDay =
    at.getFullYear() === now.getFullYear() &&
    at.getMonth() === now.getMonth() &&
    at.getDate() === now.getDate();
  const clock = at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  if (sameDay) return clock;
  return `${at.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} ${clock}`;
}
